import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { savingsGoalsApi } from '@/lib/api'
import { SavingsGoal, CreateSavingsGoalData, BankAccount } from '@/types'
import { formatCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Pencil, Trash2, Target, CalendarClock, Link2 } from 'lucide-react'
import { differenceInDays, parseISO } from 'date-fns'

const COLOR_OPTIONS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#06B6D4', '#F97316',
]

function GoalCard({
  goal,
  onEdit,
  onDelete,
  onUpdateAmount,
}: {
  goal: SavingsGoal
  onEdit: (g: SavingsGoal) => void
  onDelete: (id: number) => void
  onUpdateAmount: (g: SavingsGoal) => void
}) {
  const pct = Math.min(100, Math.round((goal.current_amount / goal.target_amount) * 100))
  const daysLeft = goal.target_date
    ? differenceInDays(parseISO(goal.target_date), new Date())
    : null

  return (
    <Card className="relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: goal.color }} />
      <CardHeader className="pb-2 pt-5 flex flex-row items-start justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="h-4 w-4" style={{ color: goal.color }} />
            {goal.name}
          </CardTitle>
          {goal.bank_account_name && (
            <CardDescription className="flex items-center gap-1 mt-0.5 text-xs">
              <Link2 className="h-3 w-3" />
              Linked: {goal.bank_account_name}
            </CardDescription>
          )}
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(goal)}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(goal.id)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="flex justify-between text-sm mb-1.5">
            <span className="font-medium">{formatCurrency(goal.current_amount)}</span>
            <span className="text-muted-foreground">of {formatCurrency(goal.target_amount)}</span>
          </div>
          <Progress value={pct} className="h-2" />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-muted-foreground">{pct}% complete</span>
            {daysLeft !== null && (
              <span className={`text-xs flex items-center gap-0.5 ${daysLeft < 0 ? 'text-red-500' : daysLeft < 30 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                <CalendarClock className="h-3 w-3" />
                {daysLeft < 0
                  ? `${Math.abs(daysLeft)} days overdue`
                  : `${daysLeft} days left`}
              </span>
            )}
          </div>
        </div>
        {!goal.bank_account_id && (
          <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => onUpdateAmount(goal)}>
            Update Progress
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

const EMPTY_FORM: CreateSavingsGoalData = {
  name: '',
  target_amount: 0,
  current_amount: 0,
  target_date: null,
  bank_account_id: null,
  color: '#3B82F6',
  icon: 'piggy-bank',
}

export default function Goals() {
  const queryClient = useQueryClient()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState<SavingsGoal | null>(null)
  const [updateAmountGoal, setUpdateAmountGoal] = useState<SavingsGoal | null>(null)
  const [newAmount, setNewAmount] = useState('')
  const [form, setForm] = useState<CreateSavingsGoalData>(EMPTY_FORM)

  const { data: goals = [], isLoading } = useQuery({
    queryKey: ['savings-goals'],
    queryFn: savingsGoalsApi.getAll,
  })

  const { data: bankAccounts = [] } = useQuery<BankAccount[]>({
    queryKey: ['bank-accounts'],
    queryFn: () => Promise.resolve([]),
  })

  const createMutation = useMutation({
    mutationFn: savingsGoalsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savings-goals'] })
      setIsCreateOpen(false)
      setForm(EMPTY_FORM)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateSavingsGoalData> }) =>
      savingsGoalsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savings-goals'] })
      setEditingGoal(null)
      setUpdateAmountGoal(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: savingsGoalsApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['savings-goals'] }),
  })

  function openEdit(goal: SavingsGoal) {
    setEditingGoal(goal)
    setForm({
      name: goal.name,
      target_amount: goal.target_amount,
      current_amount: goal.current_amount,
      target_date: goal.target_date,
      bank_account_id: goal.bank_account_id,
      color: goal.color,
      icon: goal.icon,
    })
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (editingGoal) {
      updateMutation.mutate({ id: editingGoal.id, data: form })
    } else {
      createMutation.mutate(form)
    }
  }

  const totalTarget = goals.reduce((s: number, g: SavingsGoal) => s + g.target_amount, 0)
  const totalSaved = goals.reduce((s: number, g: SavingsGoal) => s + g.current_amount, 0)
  const overallPct = totalTarget > 0 ? Math.round((totalSaved / totalTarget) * 100) : 0

  const isFormOpen = isCreateOpen || editingGoal !== null

  return (
    <div className="container py-6 space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-6 w-6" />
            Savings Goals
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Track your progress toward financial targets
          </p>
        </div>
        <Button onClick={() => { setForm(EMPTY_FORM); setIsCreateOpen(true) }}>
          <Plus className="h-4 w-4 mr-1" />
          New Goal
        </Button>
      </div>

      {goals.length > 0 && (
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-medium">Overall Progress</span>
              <span className="text-muted-foreground">
                {formatCurrency(totalSaved)} of {formatCurrency(totalTarget)} ({overallPct}%)
              </span>
            </div>
            <Progress value={overallPct} className="h-2" />
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading goals…</p>
      ) : goals.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <Target className="h-12 w-12 mb-3 opacity-25" />
          <p className="font-medium">No goals yet</p>
          <p className="text-sm mt-1">Create your first savings goal to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {goals.map((goal: SavingsGoal) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              onEdit={openEdit}
              onDelete={(id) => deleteMutation.mutate(id)}
              onUpdateAmount={(g) => { setUpdateAmountGoal(g); setNewAmount(String(g.current_amount)) }}
            />
          ))}
        </div>
      )}

      <Dialog open={isFormOpen} onOpenChange={(open) => { if (!open) { setIsCreateOpen(false); setEditingGoal(null) } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGoal ? 'Edit Goal' : 'New Savings Goal'}</DialogTitle>
            <DialogDescription>
              {editingGoal ? 'Update your savings goal details.' : 'Set a savings target to track your progress.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="goal-name">Name</Label>
              <Input
                id="goal-name"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Emergency Fund"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="goal-target">Target Amount ($)</Label>
              <Input
                id="goal-target"
                type="number"
                min={1}
                step="0.01"
                value={form.target_amount || ''}
                onChange={e => setForm(f => ({ ...f, target_amount: parseFloat(e.target.value) || 0 }))}
                required
              />
            </div>
            {!form.bank_account_id && (
              <div className="space-y-1.5">
                <Label htmlFor="goal-current">Current Amount ($)</Label>
                <Input
                  id="goal-current"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.current_amount ?? 0}
                  onChange={e => setForm(f => ({ ...f, current_amount: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="goal-date">Target Date (optional)</Label>
              <Input
                id="goal-date"
                type="date"
                value={form.target_date ?? ''}
                onChange={e => setForm(f => ({ ...f, target_date: e.target.value || null }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="goal-account">Link to Bank Account (optional)</Label>
              <Select
                value={form.bank_account_id ? String(form.bank_account_id) : 'none'}
                onValueChange={v => setForm(f => ({ ...f, bank_account_id: v === 'none' ? null : Number(v) }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="None — track manually" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None — track manually</SelectItem>
                  {(bankAccounts as BankAccount[]).map((ba) => (
                    <SelectItem key={ba.id} value={String(ba.id)}>
                      {ba.name} {ba.balance != null ? `(${formatCurrency(ba.balance)})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Color</Label>
              <div className="flex gap-2 flex-wrap">
                {COLOR_OPTIONS.map(c => (
                  <button
                    key={c}
                    type="button"
                    className={`w-7 h-7 rounded-full border-2 transition-transform ${form.color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setForm(f => ({ ...f, color: c }))}
                  />
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setIsCreateOpen(false); setEditingGoal(null) }}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {editingGoal ? 'Save Changes' : 'Create Goal'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={updateAmountGoal !== null} onOpenChange={(open) => { if (!open) setUpdateAmountGoal(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Progress</DialogTitle>
            <DialogDescription>
              Enter your current saved amount for "{updateAmountGoal?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="new-amount">Current Amount ($)</Label>
            <Input
              id="new-amount"
              type="number"
              min={0}
              step="0.01"
              value={newAmount}
              onChange={e => setNewAmount(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateAmountGoal(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (updateAmountGoal) {
                  updateMutation.mutate({
                    id: updateAmountGoal.id,
                    data: { current_amount: parseFloat(newAmount) || 0 },
                  })
                }
              }}
              disabled={updateMutation.isPending}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
